export class AuthError extends Error {
  constructor(readonly code: string, message: string) { super(message); }
}

async function post<T>(path: string, body?: unknown): Promise<T | null> {
  const response = await fetch(path, {
    method: 'POST',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  if (response.ok) return response.status === 204 ? null : ((await response.json()) as T);
  const payload = await response.json().catch(() => ({})) as { code?: string; error?: string };
  const fallback = response.status === 429 ? 'Too many attempts. Wait a few minutes and try again.' : 'Something went wrong. Try again.';
  throw new AuthError(payload.code ?? `HTTP_${response.status}`, response.status === 429 ? fallback : payload.error ?? fallback);
}

export const login = (email: string, password: string) => post<void>('/api/auth/login', { email, password });
export const signup = (email: string, password: string) =>
  post<{ confirmationRequired: boolean }>('/api/auth/signup', { email, password }) as Promise<{ confirmationRequired: boolean }>;
export const requestReset = (email: string) => post<void>('/api/auth/reset', { email });
export const updatePassword = (token: string, password: string) => post<void>('/api/auth/update-password', { token, password });
export const logout = () => post<void>('/api/auth/logout');

/** Google sign-in is a full-page redirect: the API keeps the PKCE verifier in a cookie. */
export function googleSignInUrl(next = '/'): string {
  return next === '/' ? '/api/auth/google' : `/api/auth/google?${new URLSearchParams({ next })}`;
}
